/**
 * ALERT-TARGET.UTILS
 * Go to the place where the problem of an alert can be fixed (shared by the alerts of a step and the review before the survey is created)
 */
import { NavigateFunction } from "react-router-dom"
import { store } from "@/index"
import { ProjectStep } from "@/redux/project.types"
import { STATUS_LOADED } from "@/redux/_status.types"
import { recipientsViewActions, RecipientsFilter } from "@/features/survey/entities/recipients-view/recipients-view.store"
import { userEditCurrentOffset } from "@/redux/user.actions"

//Event sent to the step that shows a text in several languages : the language to show, and the item to select if any (detail)
export const PROJECT_FIELD_LANGUAGE_EVENT: string = "project-field-language"

//A few pulses (the animation stops by itself) on the buttons that the alert asks to click
function pulseButtons(selector: string) {
  const buttons = Array.from(document.querySelectorAll(selector))
  buttons.forEach((button) => button.classList.add("hint-pulse"))
  setTimeout(() => buttons.forEach((button) => button.classList.remove("hint-pulse")), 3000)
}

//One single scroll, to the first pencil of the list once the filtered list is loaded, then a few pulses on the pencils
function scrollToFirstPencilWhenLoaded() {
  let tries = 0
  setTimeout(() => {
    const timer = setInterval(() => {
      tries++
      const pencil = document.querySelector(".recipient-edit")
      if ((store.getState().user.status === STATUS_LOADED && pencil) || tries > 30) {
        clearInterval(timer)
        pencil?.scrollIntoView({ behavior: "smooth", block: "center" })
        pulseButtons(".recipient-edit")
      }
    }, 150)
  }, 600)
}

interface AlertTargetContext {
  step: ProjectStep //Step the alert comes from
  navigate: NavigateFunction
  openStep: (stepId: ProjectStep) => void
}

//Go to the place where an error can be fixed (questionnaire editor on the topic, or its parameters)
export function goToAlertTarget(target: string | undefined, context: AlertTargetContext) {
  const { step, navigate, openStep } = context
  if (!target) return
  //Another step of the project, or a place of the current step (the list of the recipients for example)
  if (target.startsWith("step:")) return openStep(target.replace("step:", "") as ProjectStep)
  //The list of the recipients, filtered on the files the alert is about, with a few pulses on the buttons to edit them
  if (target.startsWith("filter:")) {
    store.dispatch(recipientsViewActions.setFilter(target.replace("filter:", "") as RecipientsFilter))
    store.dispatch(userEditCurrentOffset(0))
    //The alert is displayed by the step of the list itself (participants or import) : the step stays where it is, the list scrolls only once
    if (step !== "import" && step !== "recipients") openStep("recipients")
    scrollToFirstPencilWhenLoaded()
    return
  }
  //A field of the current step : scroll to it, give it the focus and a few pulses (the animation stops by itself)
  if (target.startsWith("focus:")) {
    //"focus:<field>|<language>|<item>" : the field of a text in several languages, shown in the language where the text is missing
    //(and, when the step has a list, the item concerned : an e-mail)
    const [fieldId, language, itemId] = target.replace("focus:", "").split("|")
    if (language) window.dispatchEvent(new CustomEvent(PROJECT_FIELD_LANGUAGE_EVENT, { detail: { language, itemId } }))
    //Once the language is shown (the field is the same element, its text changes)
    setTimeout(
      () => {
        const field = document.getElementById(fieldId)
        if (!field) return
        field.scrollIntoView({ behavior: "smooth", block: "center" })
        setTimeout(() => {
          field.focus({ preventScroll: true })
          field.classList.add("hint-pulse-field")
          setTimeout(() => field.classList.remove("hint-pulse-field"), 3000)
        }, 500)
      },
      language ? 350 : 0
    )
    return
  }
  if (target.startsWith("scroll:")) {
    return document.getElementById(target.replace("scroll:", ""))?.scrollIntoView({ behavior: "smooth", block: "center" })
  }
  if (target === "params") return navigate("/survey/template")
  if (target === "editor") return navigate("/template/editor", { state: { from: "/project/edit" } })
  const [topicId, language] = target.replace("topic:", "").split("|")
  navigate("/template/editor?topic=" + topicId + (language ? "&language=" + language : ""), {
    state: { from: "/project/edit" } //The back button of the editor returns to the project
  })
}
